import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, ArrowLeft, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";

export function BlogList() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const load = () => api.get("/admin/blog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const { data } = await api.post("/admin/blog", {
      slug: `new-post-${Date.now()}`, title: "Untitled story",
      excerpt: "", cover_image: "", content: "", author: "Happy Retreats",
      category: "Stories", tags: [], is_published: false, seo: {}
    });
    nav(`/admin/blog/${data.id}`);
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/admin/blog/${id}`);
    load();
  };

  const duplicate = async (id) => {
    const { data } = await api.post(`/admin/blog/${id}/duplicate`);
    nav(`/admin/blog/${data.id}`);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Stories</div>
          <h1 className="font-display text-4xl mt-3">Blog & real weddings</h1>
        </div>
        <button onClick={create} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm rounded-md" data-testid="new-post"><Plus className="w-4 h-4" /> New story</button>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid={`post-row-${p.slug}`}>
            <div className="aspect-[4/3] bg-zinc-100">{p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium">{p.title}</div>
                  <div className="text-xs text-zinc-500">/blog/{p.slug}</div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded-full ${p.is_published ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>{p.is_published ? "Published" : "Draft"}</span>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <Link to={`/admin/blog/${p.id}`} className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50">Edit</Link>
                <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View</a>
                <button onClick={() => duplicate(p.id)} className="text-xs px-3 py-1.5 border border-zinc-300 rounded-md inline-flex items-center gap-1 hover:bg-zinc-50" data-testid={`duplicate-post-${p.slug}`}><Copy className="w-3 h-3" /> Duplicate</button>
                <button onClick={() => remove(p.id)} className="ml-auto text-red-600 text-xs px-3 py-1.5 hover:bg-red-50 rounded-md"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BlogEdit() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/admin/blog/${id}`).then((r) => setP(r.data)); }, [id]);
  if (!p) return <div>Loading…</div>;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const setS = (k, v) => setP((x) => ({ ...x, seo: { ...(x.seo || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/blog/${id}`, p); toast.success("Saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <Link to="/admin/blog" className="text-xs text-zinc-500 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All stories</Link>
      <div className="flex items-end justify-between mt-3 gap-4 flex-wrap">
        <h1 className="font-display text-4xl">{p.title}</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm"><Switch checked={p.is_published} onCheckedChange={(v) => set("is_published", v)} data-testid="post-published" /> Published</label>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2.5 text-sm rounded-md" data-testid="post-save"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Input value={p.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Title" />
          <Input value={p.slug || ""} onChange={(e) => set("slug", e.target.value)} placeholder="Slug" />
          <Textarea value={p.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} placeholder="Excerpt" rows={3} />
          <ImageUploader label="Cover image" value={p.cover_image} onChange={(v) => set("cover_image", v)} testId="post-cover" />
          <Textarea value={p.content || ""} onChange={(e) => set("content", e.target.value)} placeholder="Body (supports line breaks)" rows={16} />
        </div>
        <div className="space-y-4">
          <Input value={p.author || ""} onChange={(e) => set("author", e.target.value)} placeholder="Author" />
          <Input value={p.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="Category" />
          <Input value={(p.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Tags (comma-separated)" />
          <div className="border-t pt-4 mt-4">
            <div className="text-sm font-medium mb-2">SEO</div>
            <Input value={p.seo?.meta_title || ""} onChange={(e) => setS("meta_title", e.target.value)} placeholder="Meta title" />
            <Textarea value={p.seo?.meta_description || ""} onChange={(e) => setS("meta_description", e.target.value)} placeholder="Meta description" rows={3} className="mt-2" />
            <Input value={p.seo?.keywords || ""} onChange={(e) => setS("keywords", e.target.value)} placeholder="Keywords" className="mt-2" />
          </div>
        </div>
      </div>
    </div>
  );
}
